import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// AppTag type matching the database schema
export interface AppTag {
  id: string;
  tag_name: string;
  tag_description: string;
  version_number: number;
  version_hash: string;
  is_active: boolean;
  model: string | null;
  created_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Form data type for creating/updating tags
export interface AppTagFormData {
  tag_name: string;
  tag_description: string;
  model?: string;
  created_by?: string;
  notes?: string;
}

// Simple hash function for version tracking
const generateVersionHash = (tag_name: string, tag_description: string): string => {
  const content = `${tag_name}:${tag_description}`;
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
};

// Fetch all app_tags
export const useFetchAppTags = () => {
  return useQuery({
    queryKey: ['app_tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_tags')
        .select('*')
        .order('tag_name', { ascending: true });

      if (error) throw error;
      return data as AppTag[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Create a new app_tag
export const useCreateAppTag = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (formData: AppTagFormData) => {
      const version_hash = generateVersionHash(formData.tag_name, formData.tag_description);

      const { data, error } = await supabase
        .from('app_tags')
        .insert([{
          tag_name: formData.tag_name.trim(),
          tag_description: formData.tag_description.trim(),
          version_number: 1,
          version_hash,
          is_active: true,
          model: formData.model?.trim() || null,
          created_by: formData.created_by?.trim() || null,
          notes: formData.notes?.trim() || null,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as AppTag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app_tags'] });
      toast({
        title: "Success",
        description: "Tag created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create tag. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// Update an existing app_tag
export const useUpdateAppTag = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, formData, currentVersion }: {
      id: string;
      formData: AppTagFormData & { is_active?: boolean };
      currentVersion: number;
    }) => {
      const version_hash = generateVersionHash(formData.tag_name, formData.tag_description);

      const { data, error } = await supabase
        .from('app_tags')
        .update({
          tag_name: formData.tag_name.trim(),
          tag_description: formData.tag_description.trim(),
          version_number: currentVersion + 1,
          version_hash,
          is_active: formData.is_active !== undefined ? formData.is_active : undefined,
          model: formData.model?.trim() || null,
          created_by: formData.created_by?.trim() || null,
          notes: formData.notes?.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as AppTag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app_tags'] });
      toast({
        title: "Success",
        description: "Tag updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update tag. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// Delete an app_tag (soft or hard delete)
export const useDeleteAppTag = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, hardDelete = false }: { id: string; hardDelete?: boolean }) => {
      if (hardDelete) {
        const { error } = await supabase
          .from('app_tags')
          .delete()
          .eq('id', id);

        if (error) throw error;
      } else {
        // Soft delete by setting is_active to false
        const { error } = await supabase
          .from('app_tags')
          .update({ is_active: false, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['app_tags'] });
      toast({
        title: "Success",
        description: variables.hardDelete ? "Tag deleted permanently" : "Tag deactivated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete tag. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// Toggle is_active status
export const useToggleAppTagStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('app_tags')
        .update({ is_active, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as AppTag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app_tags'] });
      toast({
        title: "Success",
        description: "Tag status updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update tag status. Please try again.",
        variant: "destructive",
      });
    },
  });
};
